import { useEffect, useState } from "react";

/**
 * useDevice — lightweight responsive + device-type detection.
 *
 *  Returns: {
 *    isMobile, isTablet, isDesktop,         // size-based (CSS-style breakpoints)
 *    isTouch,                                // touch capability
 *    isPhone, isIpad, isAndroidPhone,        // UA-based device family hints
 *    isIOS, isAndroid,                       // OS
 *    width, height,                          // viewport
 *    reduceMotion,                           // respect user OS preference
 *  }
 *
 * Notes:
 *  - Breakpoints follow Tailwind: mobile <768, tablet 768-1023, desktop ≥1024
 *  - The hook never causes layout flicker — it reads once synchronously on
 *    mount via lazy initializer and updates only on resize.
 */
const getInitial = () => {
  if (typeof window === "undefined") {
    return {
      width: 1280,
      height: 800,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isTouch: false,
      isPhone: false,
      isIpad: false,
      isAndroidPhone: false,
      isIOS: false,
      isAndroid: false,
      reduceMotion: false,
    };
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  const ua = navigator.userAgent || "";
  const isTouch =
    "ontouchstart" in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIpadUA = /iPad/.test(ua);
  // Modern iPads identify as Mac; detect via touch + Mac
  const isIpadMacUA =
    /Macintosh/.test(ua) && isTouch && navigator.maxTouchPoints > 1;
  const isIpad = isIpadUA || isIpadMacUA;
  const isAndroid = /Android/.test(ua);
  const isAndroidPhone = isAndroid && /Mobile/.test(ua);
  const isPhone = (/iPhone|iPod/.test(ua) || isAndroidPhone) && !isIpad;
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return {
    width: w,
    height: h,
    isMobile: w < 768,
    isTablet: w >= 768 && w < 1024,
    isDesktop: w >= 1024,
    isTouch: !!isTouch,
    isPhone,
    isIpad,
    isAndroidPhone,
    isIOS,
    isAndroid,
    reduceMotion,
  };
};

export function useDevice() {
  const [state, setState] = useState(getInitial);

  useEffect(() => {
    // `getInitial` is a module-level constant (never recreated), so the empty
    // deps array is intentional — listeners attach once on mount, detach on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (typeof window === "undefined") return;
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => setState(getInitial()), 80);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    // Respond to motion-preference change
    let mq;
    if (window.matchMedia) {
      mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mq.addEventListener) mq.addEventListener("change", onResize);
      else if (mq.addListener) mq.addListener(onResize);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (mq) {
        if (mq.removeEventListener) mq.removeEventListener("change", onResize);
        else if (mq.removeListener) mq.removeListener(onResize);
      }
    };
  }, []);

  return state;
}

export default useDevice;
